import React from "react";
import "../pages.css";

import "../pages.css";

import ReadAloud from "../../components/ReadAloud/ReadAloud";

const Implementation = ({ fontSize }) => {
  return (
    <div className="page">
      <h1 className="page-title">Implementation</h1>
      <div
        className="page-text"
        style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
      >
        <ReadAloud>
          The Human Practices team began conducting research into current
          practices in production and distribution of medical and recreational
          cannabis. This involved the creation of the manufacturing and
          cannabinoid ‘pipelines’. Therefore, this led to the conclusion that
          present-day product development and cultivation of marijuana relies on
          the use of natural plant resources that add heavily to the
          increasingly detrimental impacts of greenhouse gases on the
          environment. As such, the primary aim of Pinene Express is focused on
          making use of an alternative method of cannabinoid and terpene
          production through genetic modification of yeast.
        </ReadAloud>
      </div>
      <br />

      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Determining the Current Manufacturing Process
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          From start to finish, cannabis cultivation can last 4-8 months (N.
          Hordziejko, personal communication, July 28, 2022). The cannabis plant
          is cultivated through a cutting in a process called “mother planting”
          that allows the offspring to contain identical genetics to the
          original mother, ensuring that all plants follow the same schedule, as
          well as ensure the resulting plant will be female to prevent the use
          of expensive feminization processes that must occur when male plants
          are grown from seed (Ligaya, 2018; N. Hordziejko, personal
          communication, July 28, 2022). The small cutting from the mother plant
          is then placed in a low light and high humidity room. Once roots start
          sprouting after roughly two weeks, the plant is transferred into a
          larger base (Ligaya, 2018). Each plant is estimated to require 1.115m
          <sup>2</sup> within the greenhouse for healthy growth (Butsic &
          Brenner, 2016). Following the growth of roots, the plant enters the
          vegetative growth stage, which can last, in commercial strains, 56 to
          63 days (N. Hordziejko, personal communication, July 28, 2022). The
          watering of each cannabis plant is increased to 22.7L per day during
          the vegatative stage, as well as increasing the light cycle of the
          plants to 18 hours of direct light and 6 hours of darkness (Bauer et
          al., 2015; CleanLeaf, n.d.). When the plant has reached the optimal
          size, the lighting cycle is changed from 18 hours to 12 hours of light
          to stimulate autumn and investigates a process called “flipping the
          flower” (N. Hordziejko, personal communication, July 28, 2022). It can
          take six to ten weeks for the plant to fully flower. Furthermore, to
          begin harvesting, the flower buds are separated from the stem and
          leaves of the cannabis plant. The flower buds are dried for two weeks
          before being placed in bags within bins with controlled humidity to
          retain terpene concentrations (Ligaya, 2018). The industrial process
          of extraction uses liquid CO2 to extract cannabinoids from the
          trichomes. CO2 is used in most facilities due to its effectiveness,
          despite its increased expenses for equipment and set-up. Upon
          extraction from the flower, the terpenes and cannabinoids are
          processed into oils or for use in edible products (Lazarjani et al.,
          2021).
        </div>

        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          The team established the cannabinoid ‘pipeline’ that outlines the five
          steps involved in the production of the cannabinoids
          tetrahydrocannabinol (THC) and cannabidiol (CBD) from the starting
          component, <i>S. cerevisiae</i> - a species of yeast. Figure 1
          describes the pathway breakdown as #1 olivetolic acid (OA) is produced
          from yeast, #2 the conversion to mevalonate, #3 creation of the
          precursors for the THC and CBD cannabinoids, #4 the breakdown of
          cannabigerolic acid (CBGA), and #5 the final production of THC and
          CBD.
        </div>

        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Based on the research done for the manufacturing pipeline, we can
          identify key issues that we aim to solve with the implementation of
          our new process. These are the volume of water and area required for
          each plant to grow, as well as the large expenses that accompany the
          use of CO2 for extraction.
        </div>

        <div style={{display: "flex", alignItems: "center", flexDirection: "column"}}>
        <img
          width="80%"
          alt="terpene"
          src="https://static.igem.wiki/teams/4393/wiki/implementation/cannabinoid-production-pathway.jpg"
        />
        <figcaption style={{fontSize: `${16 + fontSize}px`, textAlign: "center"}}>Figure 1</figcaption>
        </div>
        <br />
        <div style={{display: "flex", alignItems: "center", flexDirection: "column"}}>
        <img
          width="80%"
          alt="terpene"
          src="https://static.igem.wiki/teams/4393/wiki/implementation/cannabinoid-production-breakdown.jpg"
        />
        <figcaption style={{fontSize: `${16 + fontSize}px`, textAlign: "center"}}>Figure 2</figcaption>
        </div>
        <br />
      </ReadAloud>

      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Determining Implementation
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          By using the NPP pathway of yeast to produce monoterpenes, we achieve
          a higher productivity than when using the GPP pathway instead. NPP is
          the cis-isomer of GPP and GPP is the precursor for FPP which continues
          down a pathway to produce other monoterpenes (Blatt-Janmaat & Qu,
          2021). In this sense, NPP and GPP can be used interchangeably in the
          lab for monoterpene production, but through recent research and
          studies, it was found that NPP is believably a better medium for
          monoterpene synthesis than GPP (Zhou & Pichersky, 2020). This finding
          is due to the fact that NPP is not consumed or used up by the yeast
          cells to be converted to essential metabolites during metabolism of
          cell growth. Since NPP in the yeast is not being consumed or used up,
          all of the existing NPP in the yeast can be used to produce
          monoterpenoids, thus creating a greater yield (Ignea et al., 2019).
        </div>

        <div style={{display: "flex", alignItems: "center", flexDirection: "column"}}>
        <img
          width="80%"
          alt="terpene"
          src="https://static.igem.wiki/teams/4393/wiki/implementation/implementation-terpene-pathway.png"
        />
        <figcaption style={{fontSize: `${16 + fontSize}px`, textAlign: "center"}}>Figure 3: Diagram of terpene biosynthesis in yeast (Figure extracted from Kampranis & Makris, 2012).</figcaption>
        </div>
        <br />
      </ReadAloud>

      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Proposed Implementation
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Pinene Express provides an alternative to cultivation by using yeast to biosynthesize compounds of interest in cannabis. Based on our bioreactor modelling, we would use a fed-batch reactor for yeast growth and terpene and cannabinoid production. Since our products will eventually be consumed or absorbed by humans, we would need to prove that our products are food-grade quality. Thus for real-world implementation, we would need to ensure that our yeast-produced chemicals have the same effect as the naturally synthesized ones. 
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          After production and purification, we can either ship the compounds separately or as a mixture to cannabis product manufacturers. In the first case, the manufacturer would be able to customize the ratio of ingredients for their product and target audience. In the second case, we would aim to mix the ingredients in a ratio to mimic natural cannabis extracts.
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          An additional group of end users that we hope to support are medical researchers. By providing customizable mixtures, our project helps support further research into fundamentally understanding the medical properties of cannabinoids and the extent of the “entourage effect”, which refers to the phenomenon where the whole plant extract is more effective as a treatment than the pure cannabinoids (Ferber et al., 2020).
        </div>
      </ReadAloud>
      <br />
      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Implementation Challenges
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          A challenge we could face is that some manufacturers may prefer natural extracts to artificial ones. This could either be for marketing purposes or because the flavour and quality is better. However, artificial extracts are typically cheaper than natural extracts, and this may be the case for our process. Therefore our process could appeal to manufacturers looking to create cheaper options, which would certainly appeal to some consumers.
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          For legal reasons, we’d likely have to market our product as something produced by genetically-modified organisms, which leads to another challenge: the public fear surrounding synthetic biology. There is no simple way to solve this problem, but it is generally due to the fact that they don’t fully understand the technology. It helps to be transparent about our processes and have open conversations with the public. By being receptive to feedback and comments from manufacturers, consumers, and the government, we can both improve our processes and educate the public.
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Finally, cannabis is a heavily-regulated industry. For our process to succeed, significant resources would need to be spent on navigating regulations and performing inspections to make sure we meet industry standards.
        </div>
      </ReadAloud>
      <br />
      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          The Future of our Project in iGEM
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Pinene Express is the first-ever iGEM project to tackle cannabis. The research on this wiki will help other teams understand cannabis cultivation practices and related manufacturing processes. The research and groundwork done for this project will assist future projects that involve terpene and/or cannabinoid biosynthesis and upscaling biomanufacturing processes. Since iGEM is an international community and competition, we also hope that our project reduces the stigma of tackling cannabis-related topics.
        </div>
      </ReadAloud>
      <br />
      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          References
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Bauer, S., Olson, J., Cockrill, A., van Hattem, M., Miller, L.,
          Tauzer, M., & Leppig, G. (2015). Impacts of surface water diversions
          for marijuana cultivation on aquatic habitat in four northwestern
          California watersheds. <i>PLOS ONE</i>, <i>10</i>(3).
          <a href="https://doi.org/10.1371/journal.pone.0120016">https://doi.org/10.1371/journal.pone.0120016</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Blatt-Janmaat, K., & Qu, Y. (2021). The Biochemistry of
          Phytocannabinoids and Metabolic Engineering of Their Production in
          Heterologous Systems.{" "}
          <i>International Journal of Molecular Sciences</i>, <i>22</i>(5),
          2454. <a href="https://doi.org/10.3390%2Fijms22052454">https://doi.org/10.3390%2Fijms22052454</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Butsic, V., & Brenner, J. (2016). Cannabis (Cannabis sativa or C.
          indica) agriculture and the environment: a systematic,
          spatially-explicit survey and potential impacts.{" "}
          <i>U.S. Department of Agriculture</i>, <i>11</i>(4), 44023.
          <a href="https://doi.org/10.1088/1748-9326/11/4/044023">https://doi.org/10.1088/1748-9326/11/4/044023</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          CleanLeaf Air Filtration Systems. (n.d.).{" "}
          <i>The stages of cannabis growth</i>.
          <a href="https://cleanleaf.com/the-stages-of-cannabis-growth.php">https://cleanleaf.com/the-stages-of-cannabis-growth.php</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Ferber, S. G., Namdar, D., Hen-Shoval, D., Eger, G., Koltai, H., Shoval, G., Shbiro, L., & Weller, A. (2020). The “entourage effect”: Terpenes coupled with cannabinoids for the treatment of mood disorders and anxiety disorders. <i>Current Neuropharmacology, 18</i>(2), 87–96. <a href="https://doi.org/10.2174/1570159x17666190903103923">https://doi.org/10.2174/1570159x17666190903103923</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Ignea, C., Raadam, M., Motawia, M., Makris, A., Vickers, C., &
          Kampranis, A. (2019). Orthogonal monoterpenoid biosynthesis in yeast
          constructed on an isomeric substrate. <i>Nature Communications</i>,{" "}
          <i>10</i>, 3799. <a href="https://doi.org/10.1038/s41467-019-11290-x">https://doi.org/10.1038/s41467-019-11290-x</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Kampranis, S. C., & Makris, A. M. (2012). Developing a yeast cell
          factory for the production of terpenoids.{" "}
          <i>Computational and Structural Biotechnology Journal</i>, <i>3</i>
          (4). <a href="http://dx.doi.org/10.5936/csbj.201210006">http://dx.doi.org/10.5936/csbj.201210006</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Lazarjani, M. P., Young, O., Kebede, L., & Seyfoddin, A. (2021).
          Processing and extraction methods of medicinal cannabis: A narrative
          review. <i>Journal of Cannabis Research</i>, <i>3</i>, 32.
          <a href="https://doi.org/10.1186/s42238-021-00087-9">https://doi.org/10.1186/s42238-021-00087-9</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Ligaya, A. (2018, December 26). The life cycle of a cannabis plant,
          from seed to store. <i>Financial Post</i>.
          <a href="https://financialpost.com/pmn/commodities-business-pmn/agriculture-commodities-business-pmn/the-life-cycle-of-a-cannabis-plant-from-seed-to-store-2">https://financialpost.com/pmn/commodities-business-pmn/agriculture-commodities-business-pmn/the-life-cycle-of-a-cannabis-plant-from-seed-to-store-2</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Zhou, F., & Pichersky, E. (2020). More is better: the diversity of
          terpene metabolism in plants. <i>Current Opinion in Plant Biology</i>,{" "}
          <i>55</i>, 1-10. <a href="https://doi.org/10.1016/j.pbi.2020.01.005">https://doi.org/10.1016/j.pbi.2020.01.005</a>
        </div>
      </ReadAloud>
    </div>
  );
};

export default Implementation;
